/**
 * CascaderDropdown 下拉级联面板组件
 * 将 Cascader 级联选择器和 DropdownProxy 下拉代理组合在一起
 * 支持两个组件的所有原有参数
 * 参考 MultipleDropdown 的 DOM 结构和 slot 参数
 */
(function (window, $) {
  "use strict";

  /**
   * CascaderDropdown 构造函数
   * @param {Object}options - 配置选项
   *
   * Cascader 相关参数:
   * @param {Array} options.data - 级联数据
   * @param {boolean}options.multiple - 是否多选
   * @param {boolean} options.onlyLeaf - 是否只能选择叶子节点
   * @param {string} options.expandTrigger - 展开触发方式: 'click' | 'hover'
   * @param {*} options.value - 初始值
   * @param {*} options.defaultValue - 默认值
   * @param {string} options.panelHeight - 级联面板高度
   * @param {string} options.panelWidth - 每个面板的宽度
   *
   * DropdownProxy 相关参数:
   * @param {jQuery}options.$input - 输入框元素（必需）
   * @param {jQuery} options.$reference - 参考元素（默认为 $input）
   * @param {string} options.trigger - 触发方式：'click' | 'hover' | 'custom'
   * @param {string} options.minWidth - 最小宽度：'auto' | 'inherit' | 具体值
   * @param {string}options.maxWidth - 最大宽度
   * @param {string}options.placement - 弹出位置：'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end'
   * @param {boolean}options.placementFixed - 是否固定 placement 方向
   * @param {boolean}options.animation - 是否启用动画
   * @param {number} options.offset - 偏移距离（像素）
   *
   * Slot 相关参数:
   * @param {Function|string|jQuery|HTMLElement}options.headerSlot - 自定义头部内容
   * @param {Function|string|jQuery|HTMLElement}options.footerSlot - 自定义底部内容
   * @param {string} options.dropdownClass - 自定义下拉框样式类
   */
  function CascaderDropdown(options) {
    if (!options.$input || options.$input.length === 0) {
      throw new Error("CascaderDropdown [Error] => $input is required");
    }

    // 保存配置
    this.options = options;
    this.$input = options.$input;

    // 事件发射器
    this.emitter = window.useEmitter();

    // DOM 元素
    this.$dropdownContent = null;
    this.$header = null;
    this.$body = null;
    this.$footer = null;

    // 初始化
    this.init();
  }

  // ==================== 初始化 ====================

  CascaderDropdown.prototype.init = function () {
    var self = this;
    var options = this.options;

    // 创建 DropdownProxy 实例
    this.dropdown = new window.DropDownProxy({
      $input: options.$input,
      $reference: options.$reference,
      trigger: options.trigger || "click",
      minWidth: options.minWidth,
      maxWidth: options.maxWidth,
      placement: options.placement || "bottom-start",
      placementFixed: options.placementFixed || false,
      animation: options.animation !== false,
      offset: options.offset || 4,
    });

    // 创建下拉内容容器
    this.$dropdownContent = $("<div>")
      .addClass("cascader-dropdown-content")
      .addClass(options.dropdownClass || "");

    // 将内容容器添加到 DropdownProxy 的 $html 中
    this.dropdown.$html.append(this.$dropdownContent);

    // 渲染头部
    this.renderHeader();

    // 创建中间内容容器（body）
    this.$body = $("<div>").addClass("cascader-dropdown-body");
    this.$dropdownContent.append(this.$body);

    // 创建级联选择器
    this.cascader = new window.Cascader({
      data: options.data || [],
      multiple: options.multiple || false,
      onlyLeaf: options.onlyLeaf !== false,
      expandTrigger: options.expandTrigger || "click",
      value: options.value,
      defaultValue: options.defaultValue,
      panelWidth: options.panelWidth, // 透传 panelWidth 参数
      panelHeight: options.panelHeight, // 透传 panelHeight 参数
    });

    // 设置级联选择器的高度
    this.cascader.$html.css({
      "box-sizing": "border-box",
    });

    // 将级联选择器添加到 body 中
    this.$body.append(this.cascader.$html);

    // 渲染底部
    this.renderFooter();

    // 监听级联选择器的变化事件
    this.cascader.emitter.on("change", function (value) {
      self.emitter.emit("change", value);

      // 单选模式下选择后自动关闭
      if (!self.cascader.multiple) {
        self.dropdown.dropdownHide();
      }
    });

    // 监听下拉框显示/隐藏事件
    this.dropdown.emitter.on("toggleShow", function (isShow) {
      self.emitter.emit("toggleShow", isShow);
    });
  };

  // ==================== 渲染 Slot ====================

  /**
   * 渲染头部 Slot
   */
  CascaderDropdown.prototype.renderHeader = function () {
    var headerSlot = this.options.headerSlot;

    if (!headerSlot) return;

    this.$header = $("<div>").addClass("cascader-dropdown-header");

    if (typeof headerSlot === "function") {
      var content = headerSlot();
      if (typeof content === "string") {
        this.$header.html(content);
      } else if (content instanceof $ || (content && content.nodeType)) {
        this.$header.append(content);
      }
    } else if (typeof headerSlot === "string") {
      this.$header.html(headerSlot);
    } else if (headerSlot instanceof $ || (headerSlot && headerSlot.nodeType)) {
      this.$header.append(headerSlot);
    }

    this.$dropdownContent.append(this.$header);
  };

  /**
   * 渲染底部 Slot
   */
  CascaderDropdown.prototype.renderFooter = function () {
    var footerSlot = this.options.footerSlot;

    if (!footerSlot) return;

    this.$footer = $("<div>").addClass("cascader-dropdown-footer");

    if (typeof footerSlot === "function") {
      var content = footerSlot();
      if (typeof content === "string") {
        this.$footer.html(content);
      }else if (content instanceof $ || (content && content.nodeType)) {
        this.$footer.append(content);
      }
    } else if (typeof footerSlot === "string") {
      this.$footer.html(footerSlot);
    }else if (footerSlot instanceof $ || (footerSlot && footerSlot.nodeType)) {
      this.$footer.append(footerSlot);
    }

    this.$dropdownContent.append(this.$footer);
  };

  // ==================== 公共方法 ====================

  /**
   * 获取当前选中的值
   * @returns {*}单选模式返回 {id, path, name}，多选模式返回数组
   */
  CascaderDropdown.prototype.getValue = function () {
    return this.cascader.getValue();
  };

  /**
   * 设置选中的值
   * @param {*} value - 单选模式传 {id, path, name}，多选模式传数组
   */
  CascaderDropdown.prototype.setValue = function (value) {
    this.cascader.setValue(value);
  };

  /**
   * 更新级联数据
   * @param {Array}data - 新的级联数据
   */
  CascaderDropdown.prototype.updateData = function (data) {
    this.cascader.updateData(data);
  };

  /**
   * 显示下拉框
   */
  CascaderDropdown.prototype.show = function () {
    this.dropdown.dropdownShow();
  };

  /**
   * 隐藏下拉框
   */
  CascaderDropdown.prototype.hide = function () {
    this.dropdown.dropdownHide();
  };

  /**
   * 切换下拉框显示/隐藏
   */
  CascaderDropdown.prototype.toggle = function () {
    this.dropdown.toggleShow();
  };

  /**
   * 销毁组件
   */
  CascaderDropdown.prototype.destroy = function () {
    // 销毁级联选择器
    if (this.cascader && this.cascader.destroy) {
      this.cascader.destroy();
    }

    // 移除下拉框
    if (this.dropdown && this.dropdown.$html) {
      this.dropdown.$html.remove();
    }

    // 清空引用
    this.cascader = null;
    this.dropdown = null;
    this.emitter = null;
  };

  // 导出到全局
  window.CascaderDropdown = CascaderDropdown;
})(window, jQuery);
